/**
 * Wave 317 · Littlest Gênese · types.
 *
 * The architecture is component-library composition. A small set of types
 * below is the entire contract between specs, composer, library, and
 * viewer. Every concept that crosses module boundaries is defined here.
 *
 *   Program       — the composition view of project facts (suites_count,
 *                   has_pool, etc.). Derived from `project_specs` at runtime.
 *   BlockId       — the canonical name of an authored block.
 *   BlockInstance — composer's output: which block, where to place it.
 *   ComposedScene — full output: instances + camera + bounds.
 *   Palette       — locked 12-color set; every block + every light reads
 *                   from this. Cohesion comes from this single object.
 */

export type BlockId =
  | 'ground'
  | 'sky'
  | 'suite'
  | 'pool'
  | 'garage'
  | 'garden_strip'
  | 'brise_panel'
  | 'chimney'
  | 'tree'
  | 'car';

export interface Program {
  suitesCount: number;
  hasPool: boolean;
  hasGarage: boolean;
  hasBriseFacade: boolean;
  hasChimney: boolean;
  /** Lot orientation — drives camera initial framing. */
  frontFacing: 'south' | 'north' | 'east' | 'west';
}

export interface Dimensions {
  /** Long arm of the L (where the suites + social row live). Meters. */
  houseLLongM: number;
  /** Short arm of the L (where garage + service live). Meters. */
  houseLShortM: number;
  /** Width of one suite. Meters. */
  suiteWM: number;
  /** Depth of one suite (north-south). Meters. */
  suiteDM: number;
}

export interface BlockInstance {
  blockId: BlockId;
  /** World position [x, y_ground, z]. Y is always 0 — blocks own their height. */
  position: [number, number, number];
  /** Rotation in radians around Y axis. */
  rotation?: number;
  /** Per-instance overrides the block respects (e.g. `width` on a suite). */
  paramOverrides?: Record<string, number | string | boolean>;
  /** Stable key for React reconciliation. */
  key: string;
}

export interface ComposedScene {
  instances: BlockInstance[];
  camera: {
    position: [number, number, number];
    target: [number, number, number];
  };
  bounds: {
    min: [number, number, number];
    max: [number, number, number];
  };
}

/**
 * Wave 317 · The locked palette. Every material in every block extends one
 * of these. Change here = change everywhere. JP's premise: encantar o
 * comprador. Warm, earthy, sunset-leaning. Not photoreal — toy-like with
 * dignity.
 */
export const PALETTE = {
  // Surfaces
  ground:        '#3d3a32',  // dark warm earth — the lot
  groundEdge:    '#272520',  // outer ground frame
  grass:         '#7a8757',  // garden strip green
  deck:          '#5e3d22',  // ipê deck around pool
  // Materials
  wallSocial:    '#e2d4b8',  // social wing walls (warm cream)
  wallSuites:    '#d4c4a3',  // suites walls (slightly darker)
  wallWet:       '#a08a6e',  // service / banhos
  roof:          '#1f1c18',  // dark metal slab
  brise:         '#7a5230',  // cumaru wood brise
  glass:         '#9ec6e6',  // tinted glass (translucent)
  // Water + accents
  pool:          '#3e9bbf',  // pool water (will UV-scroll)
  prainha:       '#6dbed9',  // shallow prainha
  // Light + ambient
  sky:           '#f4c97a',  // sunset peach
  skyTop:        '#1d2840',  // dusk indigo
  emissiveDusk:  '#ffd4a3',  // suite windows at dusk
  smoke:         '#d8d2c8',  // chimney wisp
  // Ink
  ink:           '#1a1612',  // labels + outlines
  // Brand stripe
  brandWarm:     '#ff9a3e',  // accent — used sparingly
} as const;

export type PaletteKey = keyof typeof PALETTE;
